import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  childrensBookAuthoringPageAspectRatio,
} from "../utils/childrensBookAuthoringPageGeometry";

import {
  buildTextbookFigureTextFlowUnits,
} from "../utils/textbookFigureTextFlow";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


export function textbookDerivedChapterPageCount({
  scrollWidth = 0,
  contentWidth = 0,
  pagePitch = 0,
} = {}) {
  const normalizedPagePitch =
    Number(
      pagePitch
    );

  if (
    !Number.isFinite(
      normalizedPagePitch
    ) ||
    normalizedPagePitch <= 0
  ) {
    return 1;
  }

  const overflowWidth =
    Math.max(
      0,
      Number(
        scrollWidth || 0
      ) -
      Number(
        contentWidth || 0
      )
    );

  return Math.max(
    1,
    Math.round(
      overflowWidth /
      normalizedPagePitch
    ) + 1
  );
}


function trimLabel(value) {
  const trim =
    clean(value);

  if (!trim) {
    return "";
  }

  if (
    trim.toLowerCase() ===
      "a4" ||
    trim.toLowerCase() ===
      "a5"
  ) {
    return trim.toUpperCase();
  }

  return trim
    .replace(
      /_x_/gi,
      " × "
    )
    .replace(
      /_in$/i,
      " in"
    )
    .replace(
      /_/g,
      " "
    );
}


function pageGeometry(
  formatPlan = {}
) {
  const ratio =
    childrensBookAuthoringPageAspectRatio(
      formatPlan
    );

  if (
    !Number.isFinite(
      ratio
    ) ||
    ratio <= 0
  ) {
    return {
      ready: false,
      ratio: null,
      pageWidth: 0,
      pageHeight: 0,
      horizontalMargin: 0,
      verticalMargin: 0,
      pageGap: 0,
      contentWidth: 0,
      contentHeight: 0,
      columnGap: 0,
      pagePitch: 0,
    };
  }

  /*
   * Writer-facing screen projection only.
   * Physical publication measurement remains downstream.
   */
  /*
   * Keep the writer's Live Chapter page projection at the same
   * screen scale as Review Your Book. Both surfaces use fixed-pixel
   * typography, so different page widths would otherwise create
   * different apparent pagination for the same selected format.
   */
  const pageWidth =
    ratio >= 0.95
      ? 820
      : 760;

  const pageHeight =
    Math.round(
      pageWidth /
      ratio
    );

  const horizontalMargin =
    Math.max(
      42,
      Math.round(
        pageWidth * 0.075
      )
    );

  const verticalMargin =
    Math.max(
      46,
      Math.round(
        pageHeight * 0.07
      )
    );

  const pageGap = 28;

  const contentWidth =
    pageWidth -
    horizontalMargin * 2;

  const contentHeight =
    pageHeight -
    verticalMargin * 2;

  const columnGap =
    horizontalMargin * 2 +
    pageGap;

  return {
    ready: true,
    ratio,
    pageWidth,
    pageHeight,
    horizontalMargin,
    verticalMargin,
    pageGap,
    contentWidth,
    contentHeight,
    columnGap,
    pagePitch:
      pageWidth +
      pageGap,
  };
}


function liveTextOffsetAtPoint(
  element,
  event
) {
  if (
    !element ||
    typeof document ===
      "undefined"
  ) {
    return null;
  }

  const clientX =
    Number(
      event?.clientX
    );

  const clientY =
    Number(
      event?.clientY
    );

  if (
    !Number.isFinite(
      clientX
    ) ||
    !Number.isFinite(
      clientY
    )
  ) {
    return null;
  }

  const doc =
    element.ownerDocument ||
    document;

  let node = null;
  let offset = 0;

  if (
    typeof doc
      .caretPositionFromPoint ===
      "function"
  ) {
    const position =
      doc.caretPositionFromPoint(
        clientX,
        clientY
      );

    node =
      position?.offsetNode ||
      null;

    offset =
      Number(
        position?.offset
      ) || 0;
  } else if (
    typeof doc
      .caretRangeFromPoint ===
      "function"
  ) {
    const caret =
      doc.caretRangeFromPoint(
        clientX,
        clientY
      );

    node =
      caret?.startContainer ||
      null;

    offset =
      Number(
        caret?.startOffset
      ) || 0;
  }

  if (
    !node ||
    !element.contains(
      node
    )
  ) {
    return null;
  }

  try {
    const range =
      doc.createRange();

    range.selectNodeContents(
      element
    );

    range.setEnd(
      node,
      offset
    );

    return range
      .toString()
      .replace(
        /\u200b/g,
        ""
      )
      .length;
  } catch {
    return null;
  }
}



function liveCaretMarkerAtPoint(
  element,
  event,
  flowElement
) {
  if (
    !element ||
    !flowElement ||
    typeof document ===
      "undefined"
  ) {
    return null;
  }

  const clientX =
    Number(
      event?.clientX
    );

  const clientY =
    Number(
      event?.clientY
    );

  if (
    !Number.isFinite(
      clientX
    ) ||
    !Number.isFinite(
      clientY
    )
  ) {
    return null;
  }

  const doc =
    element.ownerDocument ||
    document;

  let range = null;

  if (
    typeof doc
      .caretPositionFromPoint ===
      "function"
  ) {
    const position =
      doc.caretPositionFromPoint(
        clientX,
        clientY
      );

    const node =
      position?.offsetNode ||
      null;

    const offset =
      Number(
        position?.offset
      ) || 0;

    if (
      node &&
      element.contains(
        node
      )
    ) {
      try {
        range =
          doc.createRange();

        range.setStart(
          node,
          offset
        );

        range.collapse(
          true
        );
      } catch {
        range = null;
      }
    }
  } else if (
    typeof doc
      .caretRangeFromPoint ===
      "function"
  ) {
    const caret =
      doc.caretRangeFromPoint(
        clientX,
        clientY
      );

    const node =
      caret
        ?.startContainer ||
      null;

    if (
      node &&
      element.contains(
        node
      )
    ) {
      try {
        range =
          caret.cloneRange
            ? caret.cloneRange()
            : caret;

        range.collapse(
          true
        );
      } catch {
        range = null;
      }
    }
  }

  const flowRect =
    flowElement
      .getBoundingClientRect?.();

  if (!flowRect) {
    return null;
  }

  let left =
    clientX -
    flowRect.left;

  let top =
    clientY -
    flowRect.top -
    10;

  let height = 20;

  const caretRect =
    range
      ?.getBoundingClientRect?.();

  if (
    caretRect &&
    Number.isFinite(
      caretRect.left
    ) &&
    Number.isFinite(
      caretRect.top
    )
  ) {
    left =
      caretRect.left -
      flowRect.left;

    top =
      caretRect.top -
      flowRect.top;

    height =
      Math.max(
        18,
        Number(
          caretRect.height
        ) || 0
      );
  }

  return {
    left:
      Math.max(
        0,
        left
      ),

    top:
      Math.max(
        0,
        top
      ),

    height,
  };
}


function unitBlock(unit) {
  if (
    unit?.kind ===
      "figure_text_flow"
  ) {
    return unit.figure || null;
  }

  if (
    [
      "rich_manuscript_figure_flow",
      "cursor_figure_flow",
    ].includes(
      unit?.kind
    )
  ) {
    return unit.paragraph || null;
  }

  return unit?.block || null;
}


function firstFragmentRect(
  element
) {
  if (!element) {
    return null;
  }

  const rects =
    element
      .getClientRects?.();

  if (
    rects &&
    rects.length
  ) {
    return rects[0];
  }

  return (
    element
      .getBoundingClientRect?.() ||
    null
  );
}


function figureFlowBoundaryBreakIds(
  flowElement,
  pagePitch
) {
  if (
    !flowElement ||
    !Number.isFinite(
      pagePitch
    ) ||
    pagePitch <= 0
  ) {
    return [];
  }

  const flowRect =
    flowElement
      .getBoundingClientRect?.();

  if (!flowRect) {
    return [];
  }

  const units =
    Array.from(
      flowElement
        .querySelectorAll?.(
          '[data-textbook-live-figure-flow-unit="true"]'
        ) || []
    );

  const columnIndex =
    (rect) =>
      Math.max(
        0,
        Math.round(
          (
            Number(
              rect?.left || 0
            ) -
            Number(
              flowRect.left || 0
            )
          ) /
          pagePitch
        )
      );

  return units
    .map(
      (unitElement) => {
        const blockId =
          clean(
            unitElement
              .getAttribute?.(
                "data-textbook-live-unit-block-id"
              )
          );

        const figures =
          Array.from(
            unitElement
              .querySelectorAll?.(
                ".ns-textbook-figure-flow-figure"
              ) || []
          );

        const manuscriptBlocks =
          Array.from(
            unitElement
              .querySelectorAll?.(
                "[data-manuscript-block-id]"
              ) || []
          );

        const fallbackTextElement =
          manuscriptBlocks[0] ||
          unitElement
            .querySelector?.(
              ".ns-textbook-figure-flow-paragraph"
            );

        if (
          !blockId ||
          figures.length === 0
        ) {
          return "";
        }

        const splitFigure =
          figures.some(
            (figure) => {
              const anchorId =
                clean(
                  figure
                    .getAttribute?.(
                      "data-textbook-figure-flow-anchor-id"
                    )
                );

              const textElement =
                anchorId
                  ? (
                      manuscriptBlocks
                        .find(
                          (
                            candidate
                          ) =>
                            clean(
                              candidate
                                .getAttribute?.(
                                  "data-manuscript-block-id"
                                )
                            ) ===
                            anchorId
                        ) ||
                      fallbackTextElement
                    )
                  : fallbackTextElement;

              const figureRect =
                figure
                  ?.getBoundingClientRect?.();

              const textRect =
                firstFragmentRect(
                  textElement
                );

              return Boolean(
                figureRect &&
                textRect &&
                columnIndex(
                  figureRect
                ) !==
                  columnIndex(
                    textRect
                  )
              );
            }
          );

        return splitFigure
          ? blockId
          : "";
      }
    )
    .filter(Boolean);
}


function liveWorkingPageBlockIds(
  flowElement,
  pagePitch,
  pageCount
) {
  if (
    !flowElement ||
    !Number.isFinite(pagePitch) ||
    pagePitch <= 0 ||
    !Number.isInteger(pageCount) ||
    pageCount < 1
  ) {
    return {};
  }

  const flowRect = flowElement.getBoundingClientRect?.();
  if (!flowRect) return {};

  const pages = {};

  Array.from(
    flowElement.querySelectorAll?.(
      "[data-textbook-live-unit-block-id]"
    ) || []
  ).forEach((element) => {
    const blockId = clean(
      element.getAttribute?.("data-textbook-live-unit-block-id")
    );

    if (!blockId) return;

    const fragments = Array.from(element.getClientRects?.() || []);
    const rects = fragments.length
      ? fragments
      : [element.getBoundingClientRect?.()].filter(Boolean);

    rects.forEach((rect) => {
      const pageIndex = Math.max(
        0,
        Math.min(
          pageCount - 1,
          Math.round(
            (Number(rect?.left || 0) - Number(flowRect.left || 0)) /
              pagePitch
          )
        )
      );

      const page = pages[pageIndex] || [];
      if (!page.includes(blockId)) {
        pages[pageIndex] = [...page, blockId];
      }
    });
  });

  return pages;
}


function liveSectionPageRanges(
  flowElement,
  pagePitch,
  pageCount
) {
  if (
    !flowElement ||
    !Number.isFinite(pagePitch) ||
    pagePitch <= 0 ||
    !Number.isInteger(pageCount) ||
    pageCount < 1
  ) {
    return {};
  }

  const flowRect = flowElement.getBoundingClientRect?.();
  if (!flowRect) {
    return {};
  }

  const ranges = {};

  Array.from(
    flowElement.querySelectorAll?.(
      "[data-textbook-live-section-item-id]"
    ) || []
  ).forEach((element) => {
    const sectionItemId =
      clean(
        element.getAttribute?.(
          "data-textbook-live-section-item-id"
        )
      );

    if (!sectionItemId) {
      return;
    }

    const fragments =
      Array.from(element.getClientRects?.() || []);
    const rects =
      fragments.length > 0
        ? fragments
        : [element.getBoundingClientRect?.()].filter(Boolean);

    rects.forEach((rect) => {
      const pageIndex =
        Math.max(
          0,
          Math.min(
            pageCount - 1,
            Math.round(
              (
                Number(rect?.left || 0) -
                Number(flowRect.left || 0)
              ) / pagePitch
            )
          )
        );

      const current = ranges[sectionItemId];
      ranges[sectionItemId] = current
        ? {
            startPage: Math.min(current.startPage, pageIndex),
            endPage: Math.max(current.endPage, pageIndex),
          }
        : {
            startPage: pageIndex,
            endPage: pageIndex,
          };
    });
  });

  return ranges;
}


function textbookLiveGroupIdForRenderedPage({
  flowElement,
  pagePitch,
  pageCount,
  pageIndex,
  blocks = [],
}) {
  const pages =
    liveWorkingPageBlockIds(
      flowElement,
      pagePitch,
      pageCount
    );

  const ids =
    Array.isArray(
      pages?.[
        pageIndex
      ]
    )
      ? pages[
          pageIndex
        ]
      : [];

  for (const blockId of ids) {
    const block =
      safeArray(
        blocks
      ).find(
        (candidate) =>
          clean(
            candidate
              ?.blockId
          ) ===
            clean(
              blockId
            )
      );

    if (!block) {
      continue;
    }

    return (
      clean(
        block
          ?.sectionItemId
      ) ||
      "__chapter__"
    );
  }

  return "__chapter__";
}


export function textbookLiveSectionIdForPage({
  sectionPageRanges = {},
  sections = [],
  pageIndex = 0,
  preferredSectionItemId = "",
}) {
  const page =
    Math.max(
      0,
      Number(
        pageIndex
      ) || 0
    );

  const preferred =
    clean(
      preferredSectionItemId
    );

  const preferredRange =
    preferred
      ? sectionPageRanges[
          preferred
        ]
      : null;

  if (
    preferredRange &&
    page >=
      Number(
        preferredRange.startPage
      ) &&
    page <=
      Number(
        preferredRange.endPage
      )
  ) {
    return preferred;
  }

  const candidates =
    safeArray(
      sections
    )
      .map(
        (
          section,
          index
        ) => {
          const sectionItemId =
            clean(
              section
                ?.itemId
            );

          const range =
            sectionPageRanges[
              sectionItemId
            ];

          return {
            sectionItemId,
            index,
            range,
          };
        }
      )
      .filter(
        (candidate) =>
          Boolean(
            candidate
              .sectionItemId
          ) &&
          candidate
            .range &&
          page >=
            Number(
              candidate
                .range
                .startPage
            ) &&
          page <=
            Number(
              candidate
                .range
                .endPage
            )
      )
      .sort(
        (
          left,
          right
        ) =>
          Number(
            right
              .range
              .startPage
          ) -
            Number(
              left
                .range
                .startPage
            ) ||
          left.index -
            right.index
      );

  return (
    candidates[0]
      ?.sectionItemId ||
    ""
  );
}


function sameStringList(
  left = [],
  right = []
) {
  return (
    left.length ===
      right.length &&
    left.every(
      (
        value,
        index
      ) =>
        value ===
        right[index]
    )
  );
}


export default function TextbookLiveChapterPageFlow({
  chapterTitle = "",
  blocks = [],
  sections = [],
  sectionLayout = [],
  formatPlan = {},
  isFR = false,
  canEdit = false,
  editMode = false,
  onStartEditing,
  insertionPoint = null,
  onSelectInsertionPoint,
  onAddFigure,
  onAddContent,
  addFigureDisabled = false,
  renderBlock,
  renderFlowUnit,
  proofMode = false,
  stickyNavigation = false,
  focusSectionItemId = "",
  focusSectionRequestKey = 0,
  focusPageIndex = null,
  focusPageRequestKey = 0,
  onWorkingPageMetricsChange,
  onActivePageChange,
}) {
  const flowRef =
    useRef(null);

  const viewportRef =
    useRef(null);

  const geometry =
    useMemo(
      () =>
        pageGeometry(
          formatPlan
        ),
      [
        formatPlan
          ?.trimSize,
        formatPlan
          ?.orientation,
      ]
    );

  const flowUnits =
    useMemo(
      () =>
        buildTextbookFigureTextFlowUnits(
          safeArray(
            blocks
          )
        ),
      [blocks]
    );

  const sectionById =
    useMemo(
      () =>
        new Map(
          safeArray(
            sections
          )
            .map(
              (
                section,
                index
              ) => [
                clean(
                  section
                    ?.itemId
                ),
                {
                  ...section,
                  sectionNumber:
                    index + 1,
                },
              ]
            )
            .filter(
              ([key]) =>
                Boolean(key)
            )
        ),
      [sections]
    );

  const startOnNewPageSectionIds =
    useMemo(
      () =>
        new Set(
          safeArray(
            sectionLayout
          )
            .filter(
              (entry) =>
                entry
                  ?.startOnNewPage ===
                true
            )
            .map(
              (entry) =>
                clean(
                  entry
                    ?.sectionItemId
                )
            )
            .filter(Boolean)
        ),
      [sectionLayout]
    );

  const [
    pageState,
    setPageState,
  ] = useState({
    pageCount: 1,
    pageIndex: 0,
  });

  const [
    proofPaginationReady,
    setProofPaginationReady,
  ] = useState(
    () => !proofMode
  );


  useEffect(() => {
    if (
      typeof onActivePageChange ===
        "function"
    ) {
      onActivePageChange(
        pageState.pageIndex
      );
    }
  }, [
    pageState.pageIndex,
    onActivePageChange,
  ]);

  const [
    selectionMarker,
    setSelectionMarker,
  ] = useState(null);


  const [
    flowBoundaryBreakIds,
    setFlowBoundaryBreakIds,
  ] = useState([]);


  useEffect(() => {
    if (
      !editMode ||
      !clean(
        insertionPoint
          ?.afterBlockId
      )
    ) {
      setSelectionMarker(
        null
      );
    }
  }, [
    editMode,
    insertionPoint
      ?.afterBlockId,
  ]);

  useEffect(() => {
    if (!geometry.ready) {
      return undefined;
    }

    const flow =
      flowRef.current;

    if (!flow) {
      return undefined;
    }

    let firstFrame = null;
    let secondFrame = null;
    let timer = null;

    const cancelPending =
      () => {
        if (
          typeof window !==
            "undefined" &&
          typeof window
            .cancelAnimationFrame ===
            "function"
        ) {
          if (
            firstFrame != null
          ) {
            window
              .cancelAnimationFrame(
                firstFrame
              );
          }

          if (
            secondFrame != null
          ) {
            window
              .cancelAnimationFrame(
                secondFrame
              );
          }
        }

        if (timer != null) {
          clearTimeout(
            timer
          );
        }

        firstFrame = null;
        secondFrame = null;
        timer = null;
      };

    const measure =
      () => {
        const next =
          figureFlowBoundaryBreakIds(
            flow,
            geometry
              .pagePitch
          );

        setFlowBoundaryBreakIds(
          (current) =>
            sameStringList(
              current,
              next
            )
              ? current
              : next
        );
      };

    const scheduleMeasure =
      () => {
        cancelPending();

        setFlowBoundaryBreakIds(
          (current) =>
            current.length
              ? []
              : current
        );

        if (
          typeof window !==
            "undefined" &&
          typeof window
            .requestAnimationFrame ===
            "function"
        ) {
          firstFrame =
            window
              .requestAnimationFrame(
                () => {
                  secondFrame =
                    window
                      .requestAnimationFrame(
                        measure
                      );
                }
              );

          return;
        }

        timer =
          setTimeout(
            measure,
            0
          );
      };

    flow.addEventListener(
      "load",
      scheduleMeasure,
      true
    );

    if (
      typeof window !==
        "undefined"
    ) {
      window.addEventListener(
        "resize",
        scheduleMeasure
      );
    }

    scheduleMeasure();

    return () => {
      cancelPending();

      flow.removeEventListener(
        "load",
        scheduleMeasure,
        true
      );

      if (
        typeof window !==
          "undefined"
      ) {
        window.removeEventListener(
          "resize",
          scheduleMeasure
        );
      }
    };
  }, [
    blocks,
    sections,
    sectionLayout,
    flowUnits,
    geometry.ready,
    geometry.pagePitch,
  ]);


  useEffect(() => {
    setPageState({
      pageCount: 1,
      pageIndex: 0,
    });

    setProofPaginationReady(
      !proofMode
    );

    viewportRef
      .current
      ?.scrollTo?.({
        left: 0,
        behavior: "auto",
      });
  }, [
    chapterTitle,
    formatPlan
      ?.trimSize,
    formatPlan
      ?.orientation,
    proofMode,
  ]);

  useEffect(() => {
    if (!geometry.ready) {
      return undefined;
    }

    const flow =
      flowRef.current;

    const viewport =
      viewportRef.current;

    if (
      !flow ||
      !viewport
    ) {
      return undefined;
    }

    let frame = null;
    let timer = null;
    let settleTimer = null;
    let resizeObserver =
      null;
    let cancelled = false;

    const proofImages =
      () =>
        Array.from(
          flow.querySelectorAll?.(
            "img"
          ) || []
        );

    const primeProofImages =
      () => {
        if (!proofMode) {
          return;
        }

        proofImages()
          .forEach(
            (image) => {
              try {
                image.loading =
                  "eager";

                image.setAttribute?.(
                  "loading",
                  "eager"
                );

                if (
                  "fetchPriority" in
                    image
                ) {
                  image.fetchPriority =
                    "high";
                }
              } catch {
                // Image priority is a proof-only refinement.
              }
            }
          );
      };

    const pendingImages =
      () =>
        proofImages()
          .some(
            (image) =>
              image.complete !==
                true
          );

    const pendingFigurePreviews =
      () =>
        Array.from(
          flow.querySelectorAll?.(
            '[data-textbook-figure-preview-loading="true"]'
          ) || []
        ).length > 0;

    const markProofUnsettled =
      () => {
        if (proofMode) {
          setProofPaginationReady(
            false
          );
        }
      };

    const scheduleProofSettled =
      () => {
        if (!proofMode) {
          return;
        }

        if (settleTimer != null) {
          clearTimeout(
            settleTimer
          );
        }

        settleTimer =
          setTimeout(
            () => {
              if (cancelled) {
                return;
              }

              const stillPendingImages =
                pendingImages();

              const stillPendingFigures =
                pendingFigurePreviews();

              if (
                stillPendingImages ||
                stillPendingFigures
              ) {
                primeProofImages();
                scheduleProofSettled();
                return;
              }

              setProofPaginationReady(
                true
              );
            },
            650
          );
      };

    let pageCountSettleTimer =
      null;

    const commitPageCount =
      () => {
        const pageCount =
          textbookDerivedChapterPageCount({
            scrollWidth:
              flow.scrollWidth,
            contentWidth:
              geometry
                .contentWidth,
            pagePitch:
              geometry
                .pagePitch,
          });

        setPageState(
          (current) => {
            const nextPageIndex =
              Math.min(
                current.pageIndex,
                pageCount - 1
              );

            if (
              current.pageCount ===
                pageCount &&
              current.pageIndex ===
                nextPageIndex
            ) {
              return current;
            }

            return {
              pageCount,
              pageIndex:
                nextPageIndex,
            };
          }
        );

        if (
          typeof onWorkingPageMetricsChange === "function"
        ) {
          onWorkingPageMetricsChange({
            pageCount,
            sectionPageRanges:
              liveSectionPageRanges(
                flow,
                geometry.pagePitch,
                pageCount
              ),
            pageBlockIds:
              liveWorkingPageBlockIds(
                flow,
                geometry.pagePitch,
                pageCount
              ),
          });
        }

        scheduleProofSettled();
      };

    const updatePageCount =
      () => {
        if (
          pageCountSettleTimer != null
        ) {
          clearTimeout(
            pageCountSettleTimer
          );
        }

        /*
         * Live Chapter can receive several ResizeObserver / image / font
         * measurements while one visual change is settling. Commit the page
         * count only after a short quiet period so 7/8-page oscillation is not
         * exposed as navigation instability.
         */
        pageCountSettleTimer =
          setTimeout(
            commitPageCount,
            180
          );
      };

    const scheduleUpdate =
      () => {
        markProofUnsettled();

        if (
          typeof window !==
            "undefined" &&
          typeof window
            .requestAnimationFrame ===
            "function"
        ) {
          if (
            frame != null &&
            typeof window
              .cancelAnimationFrame ===
              "function"
          ) {
            window
              .cancelAnimationFrame(
                frame
              );
          }

          frame =
            window
              .requestAnimationFrame(
                updatePageCount
              );
        } else {
          updatePageCount();
        }
      };

    flow.addEventListener(
      "load",
      scheduleUpdate,
      true
    );

    if (
      typeof window !==
        "undefined"
    ) {
      window.addEventListener(
        "resize",
        scheduleUpdate
      );

      if (
        typeof window
          .ResizeObserver ===
          "function"
      ) {
        resizeObserver =
          new window.ResizeObserver(
            scheduleUpdate
          );

        resizeObserver
          .observe(
            flow
          );
      }

      const fontSet =
        window.document
          ?.fonts;

      if (
        proofMode &&
        fontSet?.ready &&
        typeof fontSet
          .ready
          .then ===
          "function"
      ) {
        Promise
          .resolve(
            fontSet.ready
          )
          .then(
            () => {
              if (!cancelled) {
                scheduleUpdate();
              }
            }
          )
          .catch(
            () => {
              // Font readiness is a proof refinement only.
            }
          );
      }
    }

    primeProofImages();

    scheduleUpdate();

    timer =
      setTimeout(
        scheduleUpdate,
        240
      );

    return () => {
      cancelled = true;

      flow.removeEventListener(
        "load",
        scheduleUpdate,
        true
      );

      if (
        typeof window !==
          "undefined"
      ) {
        window.removeEventListener(
          "resize",
          scheduleUpdate
        );

        if (
          frame != null &&
          typeof window
            .cancelAnimationFrame ===
            "function"
        ) {
          window
            .cancelAnimationFrame(
              frame
            );
        }
      }

      if (timer != null) {
        clearTimeout(timer);
      }

      if (settleTimer != null) {
        clearTimeout(
          settleTimer
        );
      }

      if (
        pageCountSettleTimer != null
      ) {
        clearTimeout(
          pageCountSettleTimer
        );
      }

      resizeObserver
        ?.disconnect?.();
    };
  }, [
    blocks,
    sections,
    sectionLayout,
    flowUnits,
    geometry.ready,
    geometry.contentWidth,
    geometry.pagePitch,
    onWorkingPageMetricsChange,
    proofMode,
  ]);

  useEffect(() => {
    const requestKey =
      Number(
        focusPageRequestKey
      );

    const requestedPageIndex =
      Number(
        focusPageIndex
      );

    const viewport =
      viewportRef.current;

    if (
      !geometry.ready ||
      requestKey < 1 ||
      !viewport ||
      !Number.isInteger(
        requestedPageIndex
      ) ||
      !Number.isFinite(
        geometry.pagePitch
      ) ||
      geometry.pagePitch <= 0
    ) {
      return;
    }

    const pageIndex =
      Math.max(
        0,
        Math.min(
          pageState
            .pageCount - 1,
          requestedPageIndex
        )
      );

    setPageState(
      (current) =>
        current.pageIndex ===
          pageIndex
          ? current
          : {
              ...current,
              pageIndex,
            }
    );

    /*
     * Writer Studio pattern: direct command, direct local movement.
     * No scroll event is allowed to become navigation authority.
     */
    viewport.scrollTo?.({
      left:
        pageIndex *
        geometry.pagePitch,
      behavior: "auto",
    });
  }, [
    focusPageIndex,
    focusPageRequestKey,
    geometry.ready,
    geometry.pagePitch,
    pageState.pageCount,
  ]);


  useEffect(() => {
    if (
      !geometry.ready ||
      Number(
        focusSectionRequestKey
      ) < 1
    ) {
      return;
    }

    const requestedSectionId =
      clean(
        focusSectionItemId
      );

    if (!requestedSectionId) {
      return;
    }

    const flow =
      flowRef.current;

    const viewport =
      viewportRef.current;

    if (
      !flow ||
      !viewport ||
      !Number.isFinite(
        geometry.pagePitch
      ) ||
      geometry.pagePitch <= 0
    ) {
      return;
    }

    let pageIndex = 0;

    if (
      requestedSectionId !==
        "__chapter__"
    ) {
      const sectionElement =
        Array.from(
          flow.querySelectorAll?.(
            "[data-textbook-live-section-item-id]"
          ) || []
        ).find(
          (element) =>
            clean(
              element.getAttribute?.(
                "data-textbook-live-section-item-id"
              )
            ) ===
              requestedSectionId
        );

      if (!sectionElement) {
        return;
      }

      const flowRect =
        flow.getBoundingClientRect?.();

      const sectionFragments =
        Array.from(
          sectionElement
            .getClientRects?.() ||
            []
        );

      const sectionRect =
        sectionFragments[0] ||
        sectionElement
          .getBoundingClientRect?.();

      if (
        !flowRect ||
        !sectionRect
      ) {
        return;
      }

      pageIndex =
        Math.max(
          0,
          Math.min(
            pageState
              .pageCount - 1,
            Math.round(
              (
                Number(
                  sectionRect.left ||
                    0
                ) -
                Number(
                  flowRect.left ||
                    0
                )
              ) /
                geometry
                  .pagePitch
            )
          )
        );
    }

    setPageState(
      (current) =>
        current.pageIndex ===
          pageIndex
          ? current
          : {
              ...current,
              pageIndex,
            }
    );

    if (
      typeof onActivePageChange ===
        "function"
    ) {
      onActivePageChange(
        pageIndex
      );
    }

    viewport.scrollTo?.({
      left:
        pageIndex *
        geometry.pagePitch,
      behavior: "auto",
    });

    /*
     * Keep browser vertical position untouched.
     * Section navigation controls only the local Live Chapter viewport.
     */
  }, [
    focusSectionItemId,
    focusSectionRequestKey,
    geometry.ready,
    geometry.pagePitch,
    pageState.pageCount,
    onActivePageChange,
  ]);

  if (!geometry.ready) {
    return (
      <section
        className="ns-card ns-card-soft"
        data-textbook-live-page-flow="unavailable"
        style={{
          margin:
            "0 0 20px",
          padding: 16,
          borderRadius: 14,
        }}
      >
        <strong>
          {isFR
            ? "Pages du chapitre"
            : "Chapter pages"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin:
              "5px 0 0",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Choisissez d’abord un format de livre valide pour afficher le flux de pages du chapitre."
            : "Choose a valid book format first to see the Chapter page flow."}
        </p>
      </section>
    );
  }

  function goToPage(
    requestedIndex
  ) {
    const pageIndex =
      Math.max(
        0,
        Math.min(
          pageState
            .pageCount - 1,
          Number(
            requestedIndex
          ) || 0
        )
      );

    setPageState(
      (current) => ({
        ...current,
        pageIndex,
      })
    );

    if (
      typeof onActivePageChange ===
        "function"
    ) {
      onActivePageChange(
        pageIndex
      );
    }

    viewportRef
      .current
      ?.scrollTo?.({
        left:
          pageIndex *
          geometry.pagePitch,
        behavior: "auto",
      });
  }

  const stripWidth =
    (
      pageState.pageCount *
      geometry.pageWidth
    ) +
    (
      Math.max(
        0,
        pageState.pageCount -
          1
      ) *
      geometry.pageGap
    );

  let previousSectionId =
    "";

  return (
    <section
      data-textbook-live-page-flow="true"
      style={{
        margin:
          "0 0 22px",
        padding:
          "16px clamp(10px, 2vw, 22px) 22px",
        borderRadius: 16,
        border:
          "1px solid rgba(5,150,105,0.20)",
        background:
          "#eef2f7",
      }}
    >
      <style>
        {`
          [data-textbook-live-page-viewport="true"] {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }

          [data-textbook-live-page-viewport="true"]::-webkit-scrollbar {
            display: none;
          }

          [data-textbook-live-derived-flow="true"]
          [data-manuscript-block-type="page_break"] {
            break-after: column;
          }
        `}
      </style>

      <div
        style={{
          maxWidth:
            geometry.pageWidth,
          margin:
            "0 auto 10px",
          display: "flex",
          alignItems:
            "flex-end",
          justifyContent:
            "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing:
                "0.08em",
              textTransform:
                "uppercase",
              color: "#0f766e",
            }}
          >
            {proofMode
              ? (
                  isFR
                    ? "Pages du chapitre"
                    : "Chapter Pages"
                )
              : (
                  isFR
                    ? "Pages du chapitre en direct"
                    : "Live Chapter Pages"
                )}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              fontWeight: 700,
              color: "#475569",
            }}
          >
            {trimLabel(
              formatPlan
                ?.trimSize
            )}
            {" · "}
            {proofMode
              ? (
                  isFR
                    ? "Chapitre enregistré · Lecture seule"
                    : "Saved chapter · Read-only"
                )
              : (
                  isFR
                    ? "Les modifications non enregistrées sont incluses"
                    : "Unsaved changes included"
                )}
          </div>
        </div>

        <div
          style={{
            maxWidth: 390,
            fontSize: 12,
            lineHeight: 1.45,
            color: "#475569",
            textAlign: "right",
          }}
        >
          {proofMode
            ? (
                isFR
                  ? "NextScenes répartit le chapitre enregistré sur des pages de travail pour l’épreuve. Les limites finales sont fixées pendant la préparation de publication."
                  : "NextScenes flows the saved Chapter across working pages for proofing. Final page boundaries are set during publication preparation."
              )
            : (
                isFR
                  ? "NextScenes répartit automatiquement le contenu du chapitre sur des pages de travail. Les limites finales sont fixées pendant la préparation de publication."
                  : "NextScenes automatically flows Chapter content across working pages. Final page boundaries are set during publication preparation."
              )}
        </div>
      </div>

      {canEdit &&
      !editMode ? (
        <div
          data-textbook-live-edit-entry="true"
          style={{
            maxWidth:
              geometry.pageWidth,
            margin:
              "0 auto 10px",
            display: "flex",
            justifyContent:
              "flex-end",
          }}
        >
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={
              typeof onStartEditing ===
                "function"
                ? onStartEditing
                : undefined
            }
          >
            {isFR
              ? "Modifier le chapitre"
              : "Edit Chapter"}
          </button>
        </div>
      ) : null}

      {canEdit &&
      editMode ? (
        <div
          data-textbook-live-insertion-toolbar="true"
          style={{
            maxWidth:
              geometry.pageWidth,
            margin:
              "0 auto 10px",
            padding:
              "10px 12px",
            borderRadius: 12,
            border:
              "1px solid rgba(5,150,105,0.24)",
            background:
              "#ffffff",
            display: "flex",
            alignItems:
              "center",
            justifyContent:
              "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              minWidth: 0,
              flex:
                "1 1 320px",
            }}
          >
            <strong>
              {isFR
                ? "Ajouter du contenu"
                : "Add chapter content"}
            </strong>

            <div
              className="ns-card-meta"
              style={{
                marginTop: 3,
                lineHeight: 1.45,
              }}
            >
              {clean(
                insertionPoint
                  ?.afterBlockId
              )
                ? (
                    clean(
                      insertionPoint
                        ?.label
                    )
                      ? (
                          isFR
                            ? `Curseur sélectionné : ${clean(insertionPoint.label)}`
                            : `Selected cursor: ${clean(insertionPoint.label)}`
                        )
                      : (
                          isFR
                            ? "Un point précis est sélectionné. Vous pourrez aussi choisir la fin de la section ou du chapitre."
                            : "A precise point is selected. You can also choose the end of the section or chapter."
                        )
                  )
                : (
                    isFR
                      ? "Le curseur est facultatif. Ajoutez le contenu puis choisissez le curseur, la fin de la section ou la fin du chapitre."
                      : "Cursor placement is optional. Add content, then choose the cursor, end of section, or end of chapter."
                  )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <button
              className="ns-button ns-button-outline"
              type="button"
              disabled={
                addFigureDisabled ||
                !clean(
                  insertionPoint
                    ?.afterBlockId
                )
              }
              onClick={
                onAddFigure
              }
            >
              {isFR
                ? "+ Ajouter une Figure"
                : "+ Add Figure"}
            </button>

            <button
              className="ns-button ns-button-outline"
              type="button"
              onClick={
                onAddContent
              }
            >
              {isFR
                ? "+ Ajouter du contenu"
                : "+ Add Content"}
            </button>
          </div>
        </div>
      ) : null}

      <div
        data-textbook-live-page-navigation="true"
        data-textbook-live-page-navigation-sticky={
          stickyNavigation
            ? "true"
            : undefined
        }
        style={{
          maxWidth:
            geometry.pageWidth,
          margin:
            "0 auto 10px",
          display: "flex",
          alignItems:
            "center",
          justifyContent:
            "space-between",
          gap: 10,
          ...(stickyNavigation
            ? {
                position:
                  "sticky",
                top: 90,
                zIndex: 20,
                padding:
                  "8px 10px",
                borderRadius: 12,
                background:
                  "rgba(238,242,247,0.98)",
                boxShadow:
                  "0 8px 18px rgba(15,23,42,0.10)",
              }
            : {}),
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            (
              proofMode &&
              !proofPaginationReady
            ) ||
            pageState.pageIndex <=
              0
          }
          onClick={() =>
            goToPage(
              pageState.pageIndex -
                1
            )
          }
        >
          {isFR
            ? "Page précédente"
            : "Previous Page"}
        </button>

        <label
          data-textbook-live-page-status="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 13,
            fontWeight: 800,
            color: "#334155",
          }}
        >
          <span>
            {isFR
              ? "Page du chapitre"
              : "Chapter page"}
          </span>

          {proofMode &&
          !proofPaginationReady ? (
            <span
              data-textbook-proof-pagination-settling="true"
              role="status"
              aria-live="polite"
              style={{
                minWidth: 142,
                minHeight: 36,
                padding: "7px 12px",
                borderRadius: 8,
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#475569",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Calcul des pages…"
                : "Calculating pages…"}
            </span>
          ) : (
            <select
            data-textbook-live-page-jump="true"
            aria-label={
              isFR
                ? "Aller à une page du chapitre"
                : "Jump to chapter page"
            }
            value={
              pageState.pageIndex
            }
            onChange={(event) =>
              goToPage(
                Number(
                  event.target.value
                )
              )
            }
            style={{
              minHeight: 36,
              minWidth: 118,
              padding: "6px 30px 6px 10px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#0f172a",
              font: "inherit",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {Array.from({
              length:
                pageState.pageCount,
            }).map(
              (
                _,
                pageIndex
              ) => (
                <option
                  key={
                    `chapter-page-jump-${pageIndex + 1}`
                  }
                  value={
                    pageIndex
                  }
                >
                  {isFR
                    ? `${pageIndex + 1} sur ${pageState.pageCount}`
                    : `${pageIndex + 1} of ${pageState.pageCount}`}
                </option>
              )
            )}
            </select>
          )}
        </label>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            (
              proofMode &&
              !proofPaginationReady
            ) ||
            pageState.pageIndex >=
              pageState.pageCount -
                1
          }
          onClick={() =>
            goToPage(
              pageState.pageIndex +
                1
            )
          }
        >
          {isFR
            ? "Page suivante"
            : "Next Page"}
        </button>
      </div>

      <div
        ref={viewportRef}
        data-textbook-live-page-viewport="true"
        style={{
          width: "100%",
          maxWidth:
            geometry.pageWidth,
          height:
            geometry.pageHeight,
          margin: "0 auto",
          overflowX: "hidden",
          overflowY: "hidden",
          scrollBehavior:
            "auto",
          scrollSnapType:
            "none",
        }}
      >
        <div
          style={{
            position:
              "relative",
            width:
              stripWidth,
            height:
              geometry.pageHeight,
          }}
        >
          {Array.from({
            length:
              pageState.pageCount,
          }).map(
            (
              _,
              pageIndex
            ) => (
              <div
                key={
                  `live-page-${pageIndex + 1}`
                }
                data-textbook-live-page-surface="true"
                data-textbook-live-page-number={
                  pageIndex + 1
                }
                aria-hidden="true"
                style={{
                  position:
                    "absolute",
                  left:
                    pageIndex *
                    geometry.pagePitch,
                  top: 0,
                  width:
                    geometry.pageWidth,
                  height:
                    geometry.pageHeight,
                  background:
                    "#ffffff",
                  border:
                    "1px solid #d8dee8",
                  borderRadius: 4,
                  boxShadow:
                    "0 16px 38px rgba(47,58,76,0.10)",
                  scrollSnapAlign:
                    "start",
                }}
              />
            )
          )}

          <article
            ref={flowRef}
            data-textbook-live-derived-flow="true"
            style={{
              position:
                "absolute",
              left:
                geometry
                  .horizontalMargin,
              top:
                geometry
                  .verticalMargin,
              width:
                geometry
                  .contentWidth,
              height:
                geometry
                  .contentHeight,
              columnWidth:
                geometry
                  .contentWidth,
              columnGap:
                geometry
                  .columnGap,
              columnFill:
                "auto",
              overflow:
                "visible",
            }}
          >
            {canEdit &&
            editMode &&
            selectionMarker &&
            clean(
              insertionPoint
                ?.afterBlockId
            ) &&
            clean(
              selectionMarker
                ?.afterBlockId
            ) ===
              clean(
                insertionPoint
                  ?.afterBlockId
              ) ? (
              <span
                data-textbook-live-insertion-caret="true"
                aria-hidden="true"
                style={{
                  position:
                    "absolute",
                  left:
                    selectionMarker
                      .left,
                  top:
                    selectionMarker
                      .top,
                  width: 3,
                  height:
                    selectionMarker
                      .height,
                  minHeight: 18,
                  borderRadius:
                    999,
                  background:
                    "#059669",
                  boxShadow:
                    "0 0 0 2px rgba(5,150,105,0.18)",
                  zIndex: 12,
                  pointerEvents:
                    "none",
                }}
              >
                <span
                  style={{
                    position:
                      "absolute",
                    left: -3,
                    top: -4,
                    width: 9,
                    height: 9,
                    borderRadius:
                      "50%",
                    background:
                      "#059669",
                  }}
                />
              </span>
            ) : null}

            {clean(
              chapterTitle
            ) ? (
              <h3
                className="ns-section-title"
                style={{
                  margin:
                    "0 0 14px",
                }}
              >
                {chapterTitle}
              </h3>
            ) : null}

            {flowUnits.map(
              (
                unit,
                index
              ) => {
                const block =
                  unitBlock(
                    unit
                  );

                if (!block) {
                  return null;
                }

                const sectionItemId =
                  clean(
                    block
                      ?.sectionItemId
                  );

                const section =
                  sectionById.get(
                    sectionItemId
                  ) || null;

                const showSection =
                  Boolean(
                    sectionItemId &&
                    section &&
                    sectionItemId !==
                      previousSectionId
                  );

                const startSectionOnNewPage =
                  Boolean(
                    showSection &&
                    startOnNewPageSectionIds
                      .has(
                        sectionItemId
                      )
                  );

                previousSectionId =
                  sectionItemId;

                const flowing =
                  [
                    "figure_text_flow",
                    "rich_manuscript_figure_flow",
                    "cursor_figure_flow",
                  ].includes(
                    unit?.kind
                  );

                const unitBlockId =
                  clean(
                    block
                      ?.blockId
                  ) ||
                  `live-unit-${index}`;

                const boundaryBreak =
                  flowing &&
                  flowBoundaryBreakIds
                    .includes(
                      unitBlockId
                    );

                return (
                  <div
                    key={
                      unitBlockId
                    }
                    data-textbook-live-unit-block-id={
                      unitBlockId
                    }
                    data-textbook-live-section-item-id={
                      sectionItemId ||
                      undefined
                    }
                    data-textbook-live-section-start-new-page={
                      startSectionOnNewPage
                        ? "true"
                        : undefined
                    }
                    data-textbook-live-figure-flow-unit={
                      flowing
                        ? "true"
                        : undefined
                    }
                    data-textbook-live-boundary-split={
                      boundaryBreak
                        ? "true"
                        : undefined
                    }
                    data-textbook-live-insertion-selected={
                      clean(
                        insertionPoint
                          ?.afterBlockId
                      ) ===
                        clean(
                          block
                            ?.blockId
                        )
                        ? "true"
                        : undefined
                    }
                    onClick={
                      canEdit &&
                      editMode &&
                      typeof onSelectInsertionPoint ===
                        "function"
                        ? (
                            event
                          ) => {
                            const manuscriptNode =
                              event
                                .target
                                ?.closest?.(
                                  "[data-manuscript-block-id]"
                                );

                            const insideCurrentUnit =
                              Boolean(
                                manuscriptNode &&
                                event
                                  .currentTarget
                                  .contains(
                                    manuscriptNode
                                  )
                              );

                            const manuscriptBlockId =
                              insideCurrentUnit
                                ? clean(
                                    manuscriptNode
                                      .getAttribute(
                                        "data-manuscript-block-id"
                                      )
                                  )
                                : "";

                            const manuscriptOffset =
                              insideCurrentUnit
                                ? liveTextOffsetAtPoint(
                                    manuscriptNode,
                                    event
                                  )
                                : null;

                            const marker =
                              liveCaretMarkerAtPoint(
                                insideCurrentUnit
                                  ? manuscriptNode
                                  : event
                                      .currentTarget,
                                event,
                                flowRef.current
                              );

                            setSelectionMarker(
                              marker
                                ? {
                                    ...marker,
                                    afterBlockId:
                                      clean(
                                        block
                                          ?.blockId
                                      ),
                                  }
                                : null
                            );

                            const rawLabel =
                              clean(
                                (
                                  insideCurrentUnit
                                    ? manuscriptNode
                                        .textContent
                                    : event
                                        .currentTarget
                                        .textContent
                                ) || ""
                              )
                                .replace(
                                  /\s+/g,
                                  " "
                                );

                            onSelectInsertionPoint({
                              afterBlockId:
                                clean(
                                  block
                                    ?.blockId
                                ),

                              manuscriptBlockId,

                              manuscriptOffset,

                              workingPageIndex:
                                pageState.pageIndex,

                              sectionItemId:
                                clean(
                                  block
                                    ?.sectionItemId
                                ),

                              label:
                                rawLabel
                                  .slice(
                                    0,
                                    110
                                  ),
                            });
                          }
                        : undefined
                    }
                    style={{
                      marginBottom:
                        (
                          flowing ||
                          (
                            clean(
                              block?.type
                            ) ===
                              "paragraph" &&
                            block
                              ?.manuscriptContent !==
                              undefined
                          )
                        )
                          ? 0
                          : 14,
                      cursor:
                        canEdit &&
                        editMode
                          ? "text"
                          : undefined,
                      /*
                       * The caret marker identifies the writer's insertion
                       * point. Do not outline the entire persisted fragment:
                       * Textbook presents adjacent rich fragments as one
                       * continuous manuscript stream.
                       */
                      outline:
                        undefined,
                      outlineOffset:
                        undefined,
                      borderRadius:
                        undefined,
                      /*
                       * A split side-Figure remains measurable for diagnostics,
                       * but do not move the entire rich-text/Figure unit to the
                       * next working page. Natural column flow preserves the
                       * manuscript sequence and avoids large artificial holes.
                       */
                      breakBefore:
                        startSectionOnNewPage
                          ? "column"
                          : undefined,
                      breakInside:
                        !flowing &&
                        [
                          "figure",
                          "equation",
                        ].includes(
                          clean(
                            block
                              ?.type
                          )
                        )
                          ? "avoid-column"
                          : "auto",
                    }}
                  >
                    {showSection ? (
                      <div
                        style={{
                          borderTop:
                            "1px solid #e2e8f0",
                          paddingTop:
                            12,
                          marginTop: 2,
                          marginBottom:
                            10,
                        }}
                      >
                        <h4
                          className="ns-card-title"
                          style={{
                            margin: 0,
                            fontSize: 18,
                          }}
                        >
                          {clean(
                            section
                              ?.title
                          ) ||
                            (
                              isFR
                                ? "Section sans titre"
                                : "Untitled Section"
                            )}
                        </h4>
                      </div>
                    ) : null}

                    {flowing
                      ? renderFlowUnit?.(
                          unit
                        )
                      : renderBlock?.(
                          block
                        )}
                  </div>
                );
              }
            )}
          </article>
        </div>
      </div>
    </section>
  );
}
